import { useQuery } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'
import { toCamelCase } from '../lib/typeHelper'

export enum Granularity {
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
}

export enum RunStatus {
  SUCCESS = 'success',
  ERROR = 'error',
  CANCELLED = 'cancelled',
}

export enum Vendor {
  CLAUDE = 'CLAUDE',
  GEMINI = 'GEMINI',
  AZURE_LLM = 'AZURE_LLM',
}

/** The metric set every grouping reports — a time bucket, an agent, a model. */
export interface RunMetrics {
  runs: number
  successes: number
  errors: number
  cancelled: number
  errorRate: number
  latencyP50Ms: number | null
  latencyP95Ms: number | null
  requestsPerRun: number
  inputTokens: number
  outputTokens: number
  cacheReadTokens: number
  cacheWriteTokens: number
  tokens: number
  cacheReadShare: number
  costUsd: number
  costPerRun: number
  /** Runs whose model is in the price table. The rest have no cost, not a zero. */
  pricedRuns: number
  unpricedRuns: number
}

export interface AgentRunBucket extends RunMetrics {
  period: string
  byAgent: { agentSlug: string; runs: number }[]
}

export interface AgentRunOverview {
  granularity: Granularity
  start: string
  end: string
  totals: RunMetrics
  /** Charted agents in fixed order, with `Other` last when it applies. */
  series: string[]
  buckets: AgentRunBucket[]
}

export interface AgentModelRow extends RunMetrics {
  model: string
  vendor: Vendor
}

export interface AgentRow extends RunMetrics {
  agentSlug: string
  models: AgentModelRow[]
}

export interface ErrorGroup {
  agentSlug: string
  signature: string
  sample: string
  count: number
  firstSeen: string
  lastSeen: string
  lastRunId: string
  models: string[]
  agentRuns: number
  shareOfAgentRuns: number
}

export interface ErrorReport {
  groups: ErrorGroup[]
  truncated: boolean
  scanned: number
}

export interface AgentRunRow {
  id: string
  dateCreated: string
  agentSlug: string
  model: string
  vendor: Vendor
  status: RunStatus
  rawStatus: string
  latencyMs: number | null
  costUsd: number | null
  requests: number
  inputTokens: number
  outputTokens: number
  cacheReadTokens: number
  cacheWriteTokens: number
  tokens: number
  sessionId: string | null
  userEmail: string | null
  messagePreview: string
  errorPreview: string
}

export interface AgentRunList {
  count: number
  page: number
  pageSize: number
  results: AgentRunRow[]
}

export interface SessionRun {
  id: string
  dateCreated: string
  agentSlug: string
  status: RunStatus
  latencyMs: number | null
  messagePreview: string
}

export interface AgentRunDetail extends AgentRunRow {
  userMessage: string
  response: string
  systemPrompt: string
  error: string
  /** Raw JSON text — camelizing response keys would rewrite what was recorded. */
  metadataJson: string
  /** Every model request, tool call and retry, as raw JSON for the same reason. */
  traceJson: string
  /** Size before truncation, so a cut-down trace still says how big the run was. */
  traceBytes: number | null
  permissionMode: string
  source: string
  sessionRuns: SessionRun[]
}

export interface FilterOptions {
  agentSlugs: string[]
  models: string[]
  vendors: Vendor[]
  statuses: RunStatus[]
}

export interface AgentRunParams {
  start: string
  end: string
  granularity?: Granularity
  agents?: string[]
  models?: string[]
  vendors?: string[]
  status?: RunStatus | ''
  search?: string
}

const BASE = '/api/v1/insure/internal/agent-runs'
const browserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone

function query(params: AgentRunParams, extra: Record<string, string> = {}): string {
  const search = new URLSearchParams({
    start: params.start,
    end: params.end,
    tz: browserTimezone(),
    ...extra,
  })
  if (params.granularity) search.set('granularity', params.granularity)
  if (params.agents?.length) search.set('agents', params.agents.join(','))
  if (params.models?.length) search.set('models', params.models.join(','))
  if (params.vendors?.length) search.set('vendors', params.vendors.join(','))
  if (params.status) search.set('status', params.status)
  if (params.search) search.set('search', params.search)
  return search.toString()
}

function get<T>(path: string, params: AgentRunParams, extra?: Record<string, string>): Promise<T> {
  return axios.get(`${BASE}${path}?${query(params, extra)}`).then((r) => toCamelCase(r.data) as T)
}

export function useAgentRunOverview(params: AgentRunParams) {
  return useQuery({
    queryKey: [CacheKeys.AGENT_RUN_REPORT, 'overview', params],
    queryFn: () => get<AgentRunOverview>('/overview', params),
  })
}

export function useAgentRunAgents(params: AgentRunParams) {
  return useQuery({
    queryKey: [CacheKeys.AGENT_RUN_REPORT, 'agents', params],
    queryFn: () => get<{ agents: AgentRow[] }>('/agents', params).then((d) => d.agents),
  })
}

export function useAgentRunErrors(params: AgentRunParams) {
  return useQuery({
    queryKey: [CacheKeys.AGENT_RUN_REPORT, 'errors', params],
    queryFn: () => get<ErrorReport>('/errors', params),
  })
}

export function useAgentRunList(params: AgentRunParams, page: number) {
  return useQuery({
    queryKey: [CacheKeys.AGENT_RUN_REPORT, 'runs', params, page],
    queryFn: () => get<AgentRunList>('', params, { page: String(page) }),
  })
}

export function useAgentRunDetail(runId: string | null) {
  return useQuery({
    queryKey: [CacheKeys.AGENT_RUN_REPORT, 'run', runId],
    enabled: !!runId,
    queryFn: () => axios.get(`${BASE}/${runId}`).then((r) => toCamelCase(r.data) as AgentRunDetail),
  })
}

export function useAgentRunFilterOptions() {
  return useQuery({
    queryKey: [CacheKeys.AGENT_RUN_REPORT, 'filters'],
    queryFn: () => axios.get(`${BASE}/filters`).then((r) => toCamelCase(r.data) as FilterOptions),
  })
}
